import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

import { TranslateService } from "@ngx-translate/core";
import { NgSelectComponent } from "@ng-select/ng-select";

import { Subject } from "rxjs";

import { Field } from "@bpm/models";

import { CustomSwalService } from "app/helpers";
import { takeUntil } from "rxjs/operators";

const noop = () => {

};

export const CUSTOM_SELECT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CustomSelectFieldComponent),
  multi: true
};

@Component({
    selector: "custom-select-field",
    templateUrl: "./custom-select-field.component.html",
    styleUrls: ["./custom-select-field.component.scss"],
    providers: [CUSTOM_SELECT_CONTROL_VALUE_ACCESSOR],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomSelectFieldComponent implements ControlValueAccessor, OnDestroy {
  @ViewChild(NgSelectComponent) ref: NgSelectComponent;
  
  // Private
  private _unsubscribeAll: Subject<any>;
  private _value: any;

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  // Public
  public list: any[];
  public langEnglish: boolean=false;

  @Input() field: Field;
  @Input() form: any;
  @Input() readOnlyStatus: boolean;
  @Input() lang?: string;

  @Output() changeValue: EventEmitter<any> = new EventEmitter<any>();
  
  /**
   * Constructor
   * @param {HttpClient} _http
   */
  constructor(
    private _http: HttpClient,
    private _customSwalService: CustomSwalService,
    private _traslateService: TranslateService
  ) {
    this._unsubscribeAll = new Subject();
  }

  /**
   * Función que se ejecuta al iniciar este componente
   */
  ngOnInit(): void {

    this.langEnglish = this._traslateService.currentLang == 'en' ? true : false;
    // Si el tipo de fuente es de tipo json, lo cargamos a la lista
    if (this.field?.sourceType?.name === "json") {
      this.list = JSON.parse(this.field.source);
    } else if (
      this.field?.sourceType?.name === "api" ||
      this.field?.sourceType?.name === "api server"
    ) {
      this.loadApiList("");
    }
    
  }

  /**
   * Función que se ejecuta al finalizar de utilizar este componente
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Función que se ejecuta cada vez que presionan una tecla
   * @param e Objeto de tipo Event
   */
  onKey(e) {
    this.loadApiList(e.target.value);
  }

  get value(): any{
    return this._value;
  }

  set value(obj: any) {
    const selectedValue = this.langEnglish ? obj?.nameEnglish : obj?.name;
    if ((selectedValue!== this._value)) {
      this._value = selectedValue;
      this.onChangeCallback(selectedValue);
    }
  }

  writeValue(obj: any): void {
    if (obj !== this._value){
      this._value = obj;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.ref.setDisabledState(isDisabled);
  }

  /**
   * Evento que se ejecuta al seleccionar un valor en el select
   * @param {any} obj Indica el objeto que cambio
   */
  onChange(obj){
    this.changeValue.emit(obj);
  }

  /**
   * Función para cargar la lista desde un api
   * @param {string} value
   */
  private loadApiList(value: string): void {
    let url =
      this.field.sourceType.name == "api"
        ? this.field.source
        : `${this.field.source}/${value}`;

    this._http.get<any[]>(url).pipe(takeUntil(this._unsubscribeAll)).subscribe(
      (response) => {
        this.list = response;
      },
      (error) => {
        let field = this.langEnglish ? this.field.nameEnglish : this.field.name;
        this._traslateService.get('COMMON.CUSTOM_SELECT.ERROR_LOAD_FIELD', {field:field}).pipe(takeUntil(this._unsubscribeAll)).subscribe(value => {
          this._customSwalService.showErrorMsg(value);
      });   
      }
    );
  }
}
